<script setup>
/* eslint-disable import/no-unresolved */
import { ref, useImageSource } from '#imports';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import { useDeferredMount } from '~/composables/use-deferred-mount';

const props = defineProps({
  images: {
    type: Array,
    required: true,
  },
  size: {
    type: String,
    default: '',
  },
});

const swiper = ref();
const modules = [Navigation, Pagination];

// Galleries sit deep in the article body — defer Swiper until the reader
// actually approaches one so it never competes with early interactions.
const carouselRoot = ref();
const carouselReady = useDeferredMount(carouselRoot);

const breakpoints = {
  768: {
    spaceBetween: 16,
    height: 651,
  },
};

function setSwiper(swiperInstance) {
  swiper.value = swiperInstance;
}

function getImageAssetId(image) {
  return image.asset._ref;
}

function getImageCaption(image) {
  return `Изображение-${getImageAssetId(image)}`;
}

const getImageSource = ({ image, isMobile, dpr }) => {
  const isOriginalWidth = props.size === 'isOriginalWidthImage';
  const isColumnWidth = props.size === 'isColumnWidth';
  const isBigGallery = props.size === 'isBigGallery';
  let width;
  if (isMobile) {
    width = isOriginalWidth ? undefined : 500;
  } else {
    if (!isOriginalWidth) width = 1320;
    if (isColumnWidth) width = 820;
    if (isBigGallery) width = 1320;
    if (isOriginalWidth) width = undefined;
  }

  const ratio = 1.78;
  const height = Math.round(width / ratio);

  return useImageSource({
    image,
    width,
    height,
    dpr,
  });
};
</script>

<template>
  <div
    :class="{
      'content-carousel': true,
      [`content-carousel--${size}`]: size,
    }"
  >
    <div ref="carouselRoot" class="content-carousel__slides-container">
      <Swiper
        v-if="carouselReady"
        :modules="modules"
        :space-between="8"
        :height="195"
        :slides-per-view="'auto'"
        :grab-cursor="true"
        :centered-slides="true"
        :slide-to-clicked-slide="true"
        :breakpoints="breakpoints"
        :pagination="{
          type: 'fraction',
        }"
        @swiper="setSwiper"
      >
        <SwiperSlide
          v-for="item in props.images"
          :key="item._id"
          :class="{
            'content-carousel__slide': true,
            [`content-carousel__slide--${size}`]: size,
          }"
        >
          <picture>
            <source
              media="(min-width: 768px)"
              :srcset="`
                ${getImageSource({
                  image: item.image,
                  dpr: 1,
                })} 1x,
                ${getImageSource({
                  image: item.image,
                  dpr: 2,
                })} 2x
              `"
            />
            <img
              class="content-carousel__img"
              :alt="getImageCaption(item.image)"
              loading="lazy"
              :src="`${getImageSource({
                image: item.image,
                dpr: 1,
              })}`"
              :srcset="`
                ${getImageSource({
                  image: item.image,
                  dpr: 2,
                })} 2x
              `"
            />
          </picture>
          <div class="content-carousel__text">
            <div v-if="item.caption" class="content-carousel__caption">
              {{ item.caption }}
            </div>
            <div v-if="item.source" class="content-carousel__source">
              {{ item.source }}
            </div>
          </div>
        </SwiperSlide>
      </Swiper>
      <div v-else class="content-carousel__fallback">
        <div
          v-for="item in props.images"
          :key="item._id"
          :class="{
            'content-carousel__slide': true,
            [`content-carousel__slide--${size}`]: size,
          }"
        >
          <picture>
            <source
              media="(min-width: 768px)"
              :srcset="`
                ${getImageSource({ image: item.image, dpr: 1 })} 1x,
                ${getImageSource({ image: item.image, dpr: 2 })} 2x
              `"
            />
            <img
              class="content-carousel__img"
              :alt="getImageCaption(item.image)"
              loading="lazy"
              :src="`${getImageSource({ image: item.image, dpr: 1 })}`"
              :srcset="`${getImageSource({ image: item.image, dpr: 2 })} 2x`"
            />
          </picture>
          <div class="content-carousel__text">
            <div v-if="item.caption" class="content-carousel__caption">
              {{ item.caption }}
            </div>
            <div v-if="item.source" class="content-carousel__source">
              {{ item.source }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.content-carousel {
  margin: 40px auto;

  &__text {
    max-width: 1440px;
    margin: 18px auto 0;
  }

  &--isBigGallery {
    margin: 40px 0 40px auto;
  }

  &--isColumnWidth {
    margin: 40px 0 40px auto;

    .content-carousel__text {
      padding: 0;
    }
  }

  &__slide {
    border-radius: 16px;
    max-width: 1100px;

    &--isBigGallery {
      max-width: 1320px;
    }

    &--isColumnWidth {
      max-width: 870px;
    }
  }

  &__img {
    display: block;
    width: 100%;
    height: 100%;
    border-radius: 16px;
  }

  &__caption {
    font-size: var(--h6-font-size);
    line-height: var(--h6-line-height);
  }

  &__source {
    font-size: 14px;
    line-height: 22px;
    color: rgba(var(--black-color--rgb), 40%);
  }

  /* Pre-Swiper fallback: same slides, native scroll, no JS needed. */
  &__fallback {
    display: flex;
    gap: 8px;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
      display: none;
    }

    .content-carousel__slide {
      flex: 0 0 auto;
      scroll-snap-align: center;
    }

    .content-carousel__img {
      aspect-ratio: 16 / 9;
      object-fit: cover;
    }
  }

  @media (max-width: 1439px) {
    &__slide {
      width: calc(100vw - 50px);
    }
  }

  @media (--tablet-vp) {
    &__text {
      max-width: 1440px;
      margin: 10px auto 0;
    }
  }

  @media (--small-vp) {
    margin: 20px 0 20px auto;

    &__slide {
      width: calc(100vw - 50px);
    }

    &__img {
      border-radius: 8px;
    }

    &__text {
      margin: 26px auto 0;
      padding: 0 12px;
    }

    &__caption {
      font-size: 16px;
      line-height: 22px;
    }

    &__source {
      font-size: 12px;
      line-height: 16px;
    }
  }

  @media (--x-small-vp) {
    &__slide {
      width: calc(100vw - 50px);
    }
  }
}

.content-carousel::v-deep(.swiper-slide) {
  transition: var(--default-transition) transform,
    var(--default-transition) opacity;
}

.content-carousel::v-deep(.swiper-slide-active) {
  opacity: 1;
  transform: scale(1);
}

.content-carousel::v-deep(.swiper-slide.swiper-slide-next),
.content-carousel::v-deep(.swiper-slide.swiper-slide-prev) {
  opacity: 0.2;
  transform: scale(0.9);
  cursor: pointer;
}

.content-carousel::v-deep(.swiper-slide.swiper-slide-prev) {
  transform-origin: right center;
}

.content-carousel::v-deep(.swiper-slide.swiper-slide-next) {
  transform-origin: left center;
}

.content-carousel::v-deep(.swiper-button-next),
.content-carousel::v-deep(.swiper-button-prev) {
  color: var(--accent-color);
}

.content-carousel::v-deep(.swiper-pagination) {
  text-align: center;
  font-size: 14px;
  line-height: 22px;
  color: rgba(var(--black-color--rgb), 40%);
}

.content-carousel::v-deep(.swiper, swiper-container) {
  overflow: visible;
}
</style>
